import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Trash2, Bot, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { hasExpressedChoice, startConsentWatcher } from "@/lib/iubenda";
import { useAssistant } from "@/contexts/AssistantContext";

const CHAT_ENABLED = process.env.REACT_APP_CHAT_ENABLED === "true";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_LEN = 800;

const DISCLAIMER =
  "Assistente virtuale basato su intelligenza artificiale. Fornisce esclusivamente informazioni generali sui prodotti e sui servizi MEDISISTEM e non sostituisce il parere del medico o di un professionista sanitario. Non inserire dati sanitari, diagnosi, referti, fotografie cliniche o altre informazioni sensibili.";

// Scorciatoie specifiche per la scheda prodotto (esempi richiesti dal cliente).
const PRODUCT_SHORTCUTS = [
  { label: "Caratteristiche principali", msg: "Quali sono le caratteristiche principali di questo prodotto?" },
  { label: "Colori ed esecuzioni disponibili", msg: "Quali colori ed esecuzioni sono disponibili per questo prodotto?" },
  { label: "Materiali e classi di compressione", msg: "Quali sono i materiali e le classi di compressione di questo prodotto?" },
  { label: "Come vengono rilevate le misure?", msg: "Come vengono rilevate le misure per questo prodotto?" },
  { label: "Come richiedere informazioni?", msg: "Come posso richiedere informazioni su questo prodotto?" },
];

const DEFAULT_SHORTCUTS = [
  { label: "Scopri le soluzioni", msg: "Quali soluzioni offrite?" },
  { label: "Informazioni sui prodotti", msg: "Che prodotti avete a catalogo?" },
  { label: "Prenota una misurazione", msg: "Come posso prenotare una misurazione?" },
  { label: "Contatta MEDISISTEM", msg: "Come posso contattarvi?" },
];

// Scorciatoie in base alla pagina visitata.
const PAGE_SHORTCUTS = {
  "/prodotti": [
    { label: "Panoramica dei prodotti", msg: "Puoi darmi una panoramica dei prodotti a catalogo?" },
    { label: "Differenze tra le classi di compressione", msg: "Quali sono le differenze generali tra le classi di compressione?" },
    { label: "Come funziona la misurazione?", msg: "Come funziona la misurazione?" },
    { label: "Contatta MEDISISTEM", msg: "Come posso contattarvi?" },
  ],
  "/prodotti/schiebler": [
    { label: "Panoramica calze SCHIEBLER", msg: "Puoi darmi una panoramica delle calze SCHIEBLER?" },
    { label: "Differenze tra le classi di compressione", msg: "Quali sono le differenze generali tra le classi di compressione?" },
    { label: "Come funziona la misurazione?", msg: "Come funziona la misurazione?" },
    { label: "Contatta MEDISISTEM", msg: "Come posso contattarvi?" },
  ],
  "/prodotti/bendaggi": [
    { label: "Tipi di bendaggi disponibili", msg: "Quali tipi di bendaggi avete?" },
    { label: "Differenze tra le bende", msg: "Che differenza c'è tra bende adesive, coesive e non adesive?" },
    { label: "Come richiedere informazioni?", msg: "Come posso richiedere informazioni sui bendaggi?" },
    { label: "Contatta MEDISISTEM", msg: "Come posso contattarvi?" },
  ],
  "/prodotti/pressoterapia": [
    { label: "Che cos'è la pressoterapia?", msg: "Che cos'è la pressoterapia?" },
    { label: "Come funziona il servizio?", msg: "Come funziona il servizio di pressoterapia?" },
    { label: "Prenota una misurazione", msg: "Come posso prenotare una misurazione?" },
    { label: "Contatta MEDISISTEM", msg: "Come posso contattarvi?" },
  ],
  "/il-metodo": [
    { label: "Come funziona la misurazione?", msg: "Come funziona la misurazione?" },
    { label: "In sede o a domicilio", msg: "Che differenza c'è tra il servizio in sede e a domicilio?" },
    { label: "Prenota una misurazione", msg: "Come posso prenotare una misurazione?" },
    { label: "Contatta MEDISISTEM", msg: "Come posso contattarvi?" },
  ],
  "/chi-siamo": [
    { label: "Chi è MEDISISTEM?", msg: "Chi è MEDISISTEM?" },
    { label: "Le certificazioni", msg: "Quali certificazioni ha MEDISISTEM?" },
    { label: "I vostri servizi", msg: "Quali servizi offrite?" },
    { label: "Contatta MEDISISTEM", msg: "Come posso contattarvi?" },
  ],
  "/servizi": [
    { label: "Quali servizi offrite?", msg: "Quali servizi offrite?" },
    { label: "Come funziona la misurazione?", msg: "Come funziona la misurazione?" },
    { label: "Prenota una misurazione", msg: "Come posso prenotare una misurazione?" },
    { label: "Contatta MEDISISTEM", msg: "Come posso contattarvi?" },
  ],
  "/canali": [
    { label: "A chi vi rivolgete?", msg: "A quali canali vi rivolgete (farmacie, ortopedie, strutture, privati)?" },
    { label: "Informazioni sui prodotti", msg: "Che prodotti avete a catalogo?" },
    { label: "Contatta MEDISISTEM", msg: "Come posso contattarvi?" },
  ],
  "/contatti": [
    { label: "Dove vi trovate?", msg: "Dove si trova MEDISISTEM?" },
    { label: "Come contattarvi?", msg: "Come posso contattarvi?" },
    { label: "Prenota una misurazione", msg: "Come posso prenotare una misurazione?" },
  ],
};

const LUMAVEN_SHORTCUTS = [
  { label: "Differenze tra Linea 70 e Linea 140", msg: "Quali sono le differenze tra la Linea 70 e la Linea 140?" },
  { label: "Quali esecuzioni sono disponibili?", msg: "Quali esecuzioni sono disponibili nelle linee preventive LUMAVEN?" },
  { label: "Colori e composizione", msg: "Quali sono i colori e la composizione delle calze preventive LUMAVEN?" },
  { label: "Scopri i calzini Comfort", msg: "Parlami dei calzini Comfort LUMAVEN." },
  { label: "Come vengono rilevate le misure?", msg: "Come vengono rilevate le misure?" },
  { label: "Richiedi informazioni", msg: "Come posso richiedere informazioni?" },
];

const ANTITROMBO_SHORTCUTS = [
  { label: "A cosa serve una calza antitrombo?", msg: "A cosa serve una calza antitrombo?" },
  { label: "Quali esecuzioni sono disponibili?", msg: "Quali esecuzioni sono disponibili per la calza antitrombo LUMAVEN?" },
  { label: "Come funziona il sistema Soft Touch?", msg: "Come funziona il sistema Soft Touch?" },
  { label: "Come si rilevano le misure?", msg: "Come si rilevano le misure per la calza antitrombo?" },
  { label: "Consulta la tabella taglie", msg: "Puoi spiegarmi la tabella delle taglie della calza antitrombo?" },
  { label: "Richiedi informazioni", msg: "Come posso richiedere informazioni?" },
];

const getShortcuts = (pagePath, productCtx) => {
  if (pagePath === "/prodotti/lumaven/calza-antitrombo") return ANTITROMBO_SHORTCUTS;
  if (pagePath?.startsWith("/prodotti/lumaven")) return LUMAVEN_SHORTCUTS;
  if (productCtx) return PRODUCT_SHORTCUTS;
  return PAGE_SHORTCUTS[pagePath] || DEFAULT_SHORTCUTS;
};

// Rende testo con **grassetto** e link markdown [testo](/percorso) cliccabili.
const renderInline = (text, onNavigate, kp) => {
  const nodes = [];
  const regex = /\[([^\]]+)\]\((\/[^)]+)\)|\*\*([^*]+)\*\*/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(<span key={`${kp}-t${k++}`}>{text.slice(last, m.index)}</span>);
    if (m[1] !== undefined) {
      const to = m[2];
      nodes.push(
        <button
          key={`${kp}-l${k++}`}
          type="button"
          onClick={() => onNavigate(to)}
          data-testid="assistant-inline-link"
          className="font-semibold text-green underline underline-offset-2 hover:text-green-deep"
        >
          {m[1]}
        </button>
      );
    } else {
      nodes.push(<strong key={`${kp}-b${k++}`} className="font-semibold text-navy">{m[3]}</strong>);
    }
    last = regex.lastIndex;
  }
  if (last < text.length) nodes.push(<span key={`${kp}-t${k++}`}>{text.slice(last)}</span>);
  return nodes;
};

// Rende la risposta dell'assistente con paragrafi ed elenchi puntati.
const RichText = ({ text, onNavigate }) => {
  const lines = String(text).split("\n");
  const blocks = [];
  let bucket = null;
  lines.forEach((line, i) => {
    const t = line.trim();
    const isBullet = /^[-•]\s+/.test(t);
    if (isBullet) {
      if (!bucket) { bucket = { type: "ul", items: [] }; blocks.push(bucket); }
      bucket.items.push(t.replace(/^[-•]\s+/, ""));
    } else {
      bucket = null;
      if (t) blocks.push({ type: "p", text: t, key: i });
    }
  });
  return (
    <div className="space-y-2 leading-relaxed">
      {blocks.map((b, bi) =>
        b.type === "ul" ? (
          <ul key={`ul${bi}`} className="space-y-1">
            {b.items.map((it, ii) => (
              <li key={`li${bi}-${ii}`} className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-green" />
                <span>{renderInline(it, onNavigate, `li${bi}-${ii}`)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p key={`p${bi}`}>{renderInline(b.text, onNavigate, `p${bi}`)}</p>
        )
      )}
    </div>
  );
};

const AssistantWidget = ({ pagePath }) => {
  const navigate = useNavigate();
  const { productCtx } = useAssistant();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // solo in memoria, mai salvato
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [consentReady, setConsentReady] = useState(hasExpressedChoice());
  const [backendEnabled, setBackendEnabled] = useState(false);
  const scrollRef = useRef(null);

  const hiddenPages = ["/privacy-policy", "/cookie-policy", "/termini-e-condizioni"];
  const hiddenOnThisPage = hiddenPages.includes(pagePath);
  const shortcuts = getShortcuts(pagePath, productCtx);

  useEffect(() => {
    startConsentWatcher();
    const onConsent = () => setConsentReady(hasExpressedChoice());
    window.addEventListener("ms-consent-change", onConsent);
    return () => window.removeEventListener("ms-consent-change", onConsent);
  }, []);

  // Il flag lato backend è autoritativo: in produzione (CHAT_ENABLED=false)
  // l'assistente resta nascosto anche a prescindere dalla variabile del frontend.
  useEffect(() => {
    if (!CHAT_ENABLED) return;
    fetch(`${BACKEND}/api/assistant/status`)
      .then((r) => r.json())
      .then((d) => setBackendEnabled(!!d.enabled))
      .catch(() => setBackendEnabled(false));
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Se l'utente naviga su una pagina legale, chiudiamo il pannello.
  useEffect(() => {
    if (hiddenOnThisPage) setOpen(false);
  }, [hiddenOnThisPage]);

  if (!CHAT_ENABLED || !backendEnabled || hiddenOnThisPage) return null;

  const goTo = (to) => {
    setOpen(false);
    navigate(to);
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    if (content.length > MAX_LEN) {
      toast.error(`Messaggio troppo lungo (max ${MAX_LEN} caratteri).`);
      return;
    }
    const next = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: next.slice(-16),
          page: pagePath,
          context: productCtx?.text?.slice(0, 9000),
        }),
      });
      if (res.status === 429) {
        toast.error("Troppe richieste. Attendi qualche istante e riprova.");
        setLoading(false);
        return;
      }
      if (res.status === 503) {
        toast.error("L'assistente non è al momento disponibile.");
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error("bad status");
      const data = await res.json();
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
    } catch {
      toast.error("Al momento non riesco a rispondere. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  const clearConversation = () => {
    setMessages([]);
    setInput("");
  };

  return (
    <>
      {/* Launcher — nascosto finché non c'è una scelta sui cookie (per non coprire il banner) */}
      <AnimatePresence>
        {!open && consentReady && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.25 }}
            onClick={() => setOpen(true)}
            data-testid="assistant-launcher"
            aria-label="Apri l'assistente virtuale MEDISISTEM"
            className="fixed bottom-6 right-6 z-[55] flex h-14 w-14 items-center justify-center rounded-full bg-navy text-white shadow-[0_18px_40px_-12px_rgba(15,25,35,0.6)] transition-transform hover:scale-105"
          >
            <MessageCircle size={24} />
            <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-green" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Pannello chat */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            data-testid="assistant-panel"
            className="fixed z-[55] flex flex-col overflow-hidden rounded-2xl border border-[#E7EDF2] bg-white shadow-[0_30px_80px_-20px_rgba(15,25,35,0.5)] bottom-0 right-0 h-[100dvh] w-full sm:bottom-6 sm:right-6 sm:h-[600px] sm:max-h-[80vh] sm:w-[400px]"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-2 bg-navy px-4 py-3.5 text-white">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-green"><Bot size={20} /></span>
                <div>
                  <div className="text-sm font-bold leading-tight">Assistente virtuale MEDISISTEM</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/60">Informazioni generali</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && (
                  <button
                    onClick={clearConversation}
                    data-testid="assistant-clear"
                    aria-label="Cancella conversazione"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  data-testid="assistant-close"
                  aria-label="Chiudi l'assistente"
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Corpo */}
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto bg-cloud px-4 py-4">
              {/* Disclaimer sempre visibile all'inizio */}
              <div className="flex gap-2 rounded-xl border border-green/20 bg-green/5 p-3 text-[11px] leading-relaxed text-navy/75" data-testid="assistant-disclaimer">
                <ShieldAlert size={26} className="mt-0.5 shrink-0 text-green" />
                <span>{DISCLAIMER}</span>
              </div>

              {messages.length === 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {productCtx
                      ? `Domande rapide su ${productCtx.title}, oppure scrivimi la tua domanda.`
                      : "Come posso aiutarti? Scegli un argomento o scrivimi una domanda."}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {shortcuts.map((q, i) => (
                      <button
                        key={q.label}
                        onClick={() => send(q.msg)}
                        data-testid={`assistant-quick-${i}`}
                        className="rounded-full border border-navy/15 bg-white px-3.5 py-2 text-xs font-medium text-navy transition-colors hover:border-green hover:text-green"
                      >
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`assistant-msg-${m.role}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${
                      m.role === "user" ? "bg-navy text-white" : "border border-[#E7EDF2] bg-white text-navy"
                    }`}
                  >
                    {m.role === "assistant" ? <RichText text={m.content} onNavigate={goTo} /> : m.content}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start" data-testid="assistant-loading">
                  <div className="rounded-2xl border border-[#E7EDF2] bg-white px-4 py-3">
                    <span className="flex gap-1">
                      <span className="h-2 w-2 animate-bounce rounded-full bg-green [animation-delay:-0.3s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-green [animation-delay:-0.15s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-green" />
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-[#E7EDF2] bg-white px-3 py-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value.slice(0, MAX_LEN))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={1}
                  maxLength={MAX_LEN}
                  placeholder="Scrivi un messaggio…"
                  data-testid="assistant-input"
                  className="max-h-28 flex-1 resize-none rounded-xl border border-[#E7EDF2] bg-cloud px-3.5 py-2.5 text-sm text-navy outline-none focus:border-green"
                />
                <button
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  data-testid="assistant-send"
                  aria-label="Invia messaggio"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-green text-white transition-opacity disabled:opacity-40"
                >
                  <Send size={18} />
                </button>
              </div>
              <button
                onClick={() => goTo("/it/contatti")}
                data-testid="assistant-human"
                className="mt-2 text-[11px] text-muted-foreground underline underline-offset-2 hover:text-green"
              >
                Preferisci parlare con una persona? Contatta MEDISISTEM
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AssistantWidget;
